export type PayPalConfig = {
  client_id: string;
  currency: string;
};

type MaybeImmutableMap = {
  get?: (key: string, defaultValue?: unknown) => unknown;
  getIn?: (path: string[], defaultValue?: unknown) => unknown;
  toJS?: () => unknown;
};

const hasPayPalConfigShape = (value: unknown): value is PayPalConfig => {
  if (!value || typeof value !== "object") return false;

  const config = value as Partial<PayPalConfig>;
  return (
    typeof config.client_id === "string" &&
    config.client_id.length > 0 &&
    typeof config.currency === "string" &&
    config.currency.length > 0
  );
};

// `user.get("paypal_config")` may be a plain object or an Immutable Map.
export const toPayPalConfig = (
  paypalConfigValue: unknown,
): PayPalConfig | null => {
  if (!paypalConfigValue) return null;

  const toJS = (paypalConfigValue as MaybeImmutableMap).toJS;
  const configValue =
    typeof toJS === "function"
      ? toJS.call(paypalConfigValue)
      : paypalConfigValue;

  return hasPayPalConfigShape(configValue) ? configValue : null;
};

export const getUserPayPalConfig = (user: unknown): PayPalConfig | null => {
  const get = (user as MaybeImmutableMap | undefined)?.get;
  const paypalConfigValue =
    typeof get === "function"
      ? get.call(user, "paypal_config")
      : (user as { paypal_config?: unknown } | undefined)?.paypal_config;

  return toPayPalConfig(paypalConfigValue);
};

export const extractPayPalOrderId = (response: unknown): string => {
  const getIn = (response as MaybeImmutableMap | undefined)?.getIn;
  const orderId =
    typeof getIn === "function"
      ? getIn.call(response, ["data", "order_id"])
      : (response as { data?: { order_id?: unknown } } | undefined)?.data
          ?.order_id;

  if (typeof orderId !== "string" || orderId.length === 0) {
    throw new Error("PayPal order id is missing");
  }

  return orderId;
};

export const getPayPalPayerActionUrl = (error: unknown): string | null => {
  const payerActionUrl = (error as { data?: { payer_action_url?: unknown } })
    ?.data?.payer_action_url;

  return typeof payerActionUrl === "string" && payerActionUrl.length > 0
    ? payerActionUrl
    : null;
};
